import { Typography, withStyles, withTheme } from '@material-ui/core';

import { FilteringState, IntegratedFiltering } from '@devexpress/dx-react-grid';
import { Grid, Table, TableFilterRow, TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import { IntegratedSorting, SortingState } from '@devexpress/dx-react-grid';

import { compose } from 'recompose';
import React, { Fragment, PureComponent } from 'react';
import classNames from 'classnames';

import { formatDateGeneric } from 'utilities/common';
import Log from 'utilities/log';
import Spinner from 'modules/common/spinner.component';
import { Button } from '@mui/material';

const styles = theme => {
  const intakeRecordBorder = `1px solid ${theme.palette.grey[500]}`;
  return {
    table: {
      borderSpacing: theme.spacing(0, 2)
    },
    tableHead: {
      height: 32
    },
    cell: {
      borderTop: intakeRecordBorder,
      borderBottom: intakeRecordBorder,
      borderLeft: 0,
      borderRight: 0,
      '&:first-child': {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderBottomLeftRadius: theme.shape.borderRadius,
        borderLeft: intakeRecordBorder
      },
      '&:last-child': {
        borderTopRightRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
        borderRight: intakeRecordBorder
      }
    },
    urgencyMenuItem: {
      justifyContent: 'center'
    },
    selectInput: {
      lineHeight: 1.75,
      paddingBottom: theme.spacing(0.5),
      paddingRight: theme.spacing(4),
      paddingTop: theme.spacing(0.5)
    },
    row: {
      backgroundColor: theme.palette.common.white
    },
    noDataCell: {
      paddingBottom: theme.spacing(12),
      paddingTop: theme.spacing(12),
      textAlign: 'center'
    }
  };
};

const FILTER_DISABLED_COLUMNS = ['organization'];

class PendingAuthsGrid extends PureComponent {
  static defaultProps = {
    isLoading: false
  };

  constructor(props) {
    super(props);

    this.state = {
      columns: [
        { name: 'tempFileId', title: 'Temp File ID', getCellValue: this.getTempFileIdCellValue },
        { name: 'details', title: 'Details', getCellValue: this.getDetailsCellValue },
        { name: 'tempFileStatusId', title: 'Status', getCellValue: this.getStatusCellValue },
        { name: 'departureAddress', title: 'Departure', getCellValue: this.getDepartureAddressCellValue },
        { name: 'destinationAddress', title: 'Destination', getCellValue: this.getDestinationAddressCellValue },
        { name: 'submittedOn', title: 'Submitted On', getCellValue: this.formatDate('submittedOn', 'SubmittedOn') },
        { name: 'reloCouneslor', title: '', getCellValue: this.getSetReloCounselorCellValue }
      ],
      filters: [],
      sorting: [{ columnName: 'details', direction: 'asc' }],
      integratedFilteringColumnExtensions: [{ columnName: 'details', predicate: this.keyPredicate }]
    };

    this.changeSorting = sorting => this.setState({ sorting });
  }

  keyPredicate = (value, filter) => {
    const valueLower = (value.key || '').toLowerCase();
    const filterLower = (filter.value || '').toLowerCase();
    return valueLower.indexOf(filterLower) > -1;
  };

  getRecordValue = (record, camelKey, pascalKey) => {
    return record[camelKey] ?? record[pascalKey];
  };

  idsEqual = (left, right) => {
    if (left == null || right == null) return false;
    return String(left).toLowerCase() === String(right).toLowerCase();
  };

  getClientRecord = clientRecordId => {
    const clients = this.props.unmappedClients || [];
    return clients.find(client => this.idsEqual(client.clientRecordId, clientRecordId));
  };

  getClientName = clientRecordId => {
    const client = this.getClientRecord(clientRecordId);
    return client?.shortName || client?.clientName || client?.description || client?.legalName || null;
  };

  getEnumerationTitle = (collection, id) => {
    if (!Array.isArray(collection) || id == null) return null;
    const match = collection.find(item => this.idsEqual(item.id, id));
    return match?.title || match?.longName || match?.shortName || null;
  };

  formatDate = (camelKey, pascalKey) => record => {
    return formatDateGeneric(this.getRecordValue(record, camelKey, pascalKey));
  };

  getTempFileIdCellValue = record => {
    return this.getRecordValue(record, 'tempFileId', 'TempFileId');
  };

  cellClicked = id => {
    // this.props.history.push(`/authorizations/${id}`);
  };

  getDetailsCellValue = record => {
    const firstName = this.getRecordValue(record, 'firstName', 'FirstName');
    const middleName = this.getRecordValue(record, 'middleName', 'MiddleName');
    const lastName = this.getRecordValue(record, 'lastName', 'LastName');
    const nickName = this.getRecordValue(record, 'nickName', 'NickName');
    const clientRecordId = this.getRecordValue(record, 'clientRecordId', 'ClientRecordId');
    const tempFileId = this.getRecordValue(record, 'tempFileId', 'TempFileId');
    const clientName = this.getClientName(clientRecordId);
    const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ');
    const linkedFileNumber = this.getRecordValue(record, 'selectedSimilarAuthFileNumber', 'SelectedSimilarAuthFileNumber');
    const linkedId = this.getRecordValue(record, 'selectedSimilarAuthId', 'SelectedSimilarAuthId');
    const isConfidentialFile = this.getRecordValue(record, 'isConfidentialFile', 'IsConfidentialFile');

    return (
      <Fragment key={`${tempFileId}`}>
        <Typography>{fullName || 'Unnamed'}</Typography>
        {isConfidentialFile ? <Typography style={{ fontWeight: 700 }}>Confidential File</Typography> : null}
        <Typography>{clientName || `Client Record: ${clientRecordId || 'N/A'}`}</Typography>
        <Typography>
          {record && record.moveTypeId && this.props.enumerations.allEnums
            ? this.props.enumerations.allEnums.moveTypes.find(mt => mt.id === record.moveTypeId)?.title
            : 'N/A'}
        </Typography>
        {nickName ? <Typography>{`Preferred: ${nickName}`}</Typography> : null}
        {linkedId && linkedFileNumber ? <Typography>{`Linked File: ${linkedFileNumber} (ID: ${linkedId})`}</Typography> : null}
      </Fragment>
    );
  };

  getStatusCellValue = () => {
    return 'Submitted';
  };

  formatAddress = (record, prefix) => {
    const camelPrefix = `${prefix.charAt(0).toLowerCase()}${prefix.slice(1)}`;
    const pascalPrefix = `${prefix.charAt(0).toUpperCase()}${prefix.slice(1)}`;

    const city = this.getRecordValue(record, `${camelPrefix}City`, `${pascalPrefix}City`);
    const provinceId = this.getRecordValue(record, `${camelPrefix}ProvinceId`, `${pascalPrefix}ProvinceId`);
    const countryId = this.getRecordValue(record, `${camelPrefix}CountryId`, `${pascalPrefix}CountryId`);
    const provinces = this.props.enumerations?.allEnums?.provinces;
    const countries = this.props.enumerations?.allEnums?.countries;
    const provinceName = this.getEnumerationTitle(provinces, provinceId);
    const countryName = this.getEnumerationTitle(countries, countryId);

    return [city, provinceName, countryName].filter(Boolean).join(', ') || 'N/A';
  };

  getDepartureAddressCellValue = record => this.formatAddress(record, 'departure');

  getDestinationAddressCellValue = record => this.formatAddress(record, 'destination');

  changeFilters = filters => this.setState({ filters });

  filterCell = props => {
    return props.column.title && !FILTER_DISABLED_COLUMNS.includes(props.column.name) ? <TableFilterRow.Cell {...props} /> : null;
  };

  headerCell = props => {
    return props.column.title ? <TableHeaderRow.Cell {...props} /> : null;
  };

  headerRowComponent = props => {
    return <TableHeaderRow.Row className={this.props.classes.tableHead} {...props} />;
  };

  rowComponent = props => {
    return <Table.Row className={this.props.classes.row} {...props} />;
  };

  cellComponent = props => {
    let clickHandler = null;
    let pointerClass = null;
    // if (!['surveySent'].includes(props.column.name)) {
    //   clickHandler = () => this.props.onSelect(props.row.fileId); // Add handler for when a client is clicked
    //   pointerClass = 'handles-click';
    // }
    const className = classNames(this.props.classes.cell, pointerClass);
    return <Table.Cell onClick={clickHandler} className={className} {...props} />;
  };

  tableComponent = props => {
    return <Table.Table className={this.props.classes.table} {...props} />;
  };

  noDataCellComponent = props => {
    const content = this.props.isLoading ? <Spinner logo /> : 'No Records were returned.  Check the filters above.';
    // need to remove the getMessage function to avoid exception
    // eslint-disable-next-line no-unused-vars
    const { getMessage, ...rest } = props;
    return (
      <Table.Cell {...rest} className={this.props.classes.noDataCell}>
        <Typography>{content}</Typography>
      </Table.Cell>
    );
  };

  getSetReloCounselorCellValue = record => {
    return (
      <div className={this.props.classes.centerContent}>
        <Button
          color="secondary"
          variant="contained"
          sx={{
            maxHeight: '40px',
            marginRight: '10px',
            marginTop: '8px',
            backgroundColor: '#5D9878',
            '&:hover': { backgroundColor: '#528569' }
          }}
          onClick={() => this.props.onSelectReloCounselor(record)}
        >
          Set Counselor
        </Button>
      </div>
    );
  };

  render() {
    Log.trace('RENDER', 'authorizationsGrid');
    const { authorizations } = this.props;
    const { columns, filters, tableColumnExtensions, integratedFilteringColumnExtensions, sorting } = this.state;

    return (
      <Grid rows={authorizations} columns={columns}>
        <SortingState sorting={sorting} onSortingChange={this.changeSorting} />

        <FilteringState filters={filters} onFiltersChange={this.changeFilters} />
        <IntegratedFiltering columnExtensions={integratedFilteringColumnExtensions} />
        <IntegratedSorting />
        <Table
          columnExtensions={tableColumnExtensions}
          rowComponent={this.rowComponent}
          tableComponent={this.tableComponent}
          cellComponent={this.cellComponent}
          noDataCellComponent={this.noDataCellComponent}
        />
        <TableHeaderRow rowComponent={this.headerRowComponent} cellComponent={this.headerCell} showSortingControls />
        <TableFilterRow cellComponent={this.filterCell} />
      </Grid>
    );
  }
}

export default compose(withStyles(styles), withTheme)(PendingAuthsGrid);
